import { sql } from "drizzle-orm";
import { index, pgTable, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod/v4";

/**
 * Platform products. The Proper Tool is becoming a multi-product platform:
 * the original builder quoting tool ("builder") and a new structural-engineer
 * report tool ("engineer") share one identity/auth/admin/DB layer but each
 * tool's access is granted independently per user.
 */
export const PRODUCTS = ["builder", "engineer"] as const;
export type Product = (typeof PRODUCTS)[number];

export const PRODUCT_BUILDER: Product = "builder";
export const PRODUCT_ENGINEER: Product = "engineer";

/**
 * Per-user, per-product access grant. A user may hold zero, one, or both
 * product entitlements; access to each tool is fully independent.
 *
 *  - role:   'user' | 'admin'  (product-scoped role; the platform-wide
 *            ADMIN_EMAIL is always treated as admin regardless of rows here)
 *  - status: 'active' | 'trial' | 'inactive'  (subscription/access status for
 *            this product)
 */
export const productEntitlements = pgTable(
  "product_entitlements",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    product: varchar("product").notNull(),
    role: varchar("role").notNull().default("user"),
    status: varchar("status").notNull().default("active"),
    subscriptionTier: varchar("subscription_tier"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("product_entitlements_user_product_unique").on(table.userId, table.product),
    index("idx_product_entitlements_user").on(table.userId),
    index("idx_product_entitlements_product").on(table.product),
  ]
);

export const insertProductEntitlementSchema = createInsertSchema(productEntitlements).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertProductEntitlement = z.infer<typeof insertProductEntitlementSchema>;
export type ProductEntitlement = typeof productEntitlements.$inferSelect;
